import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getEdgeFunctionUrl } from '@/utils/supabaseUrl';

interface UserProfile {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
}

interface UserVerificationProps {
  userId: string | null;
  onVerified: (profile: UserProfile) => void;
  onError: (error: string) => void;
}

export const UserVerification: React.FC<UserVerificationProps> = ({
  userId,
  onVerified,
  onError
}) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const verifyUserAccess = async () => {
      if (!userId) {
        onError('User ID parameter is required to access this form.');
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`${getEdgeFunctionUrl('coordinator-webapp')}?user_id=${userId}&cache_bust=${Date.now()}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        });

        if (response.ok) {
          const data = await response.json();
          onVerified(data.profile);
        } else {
          const errorData = await response.json().catch(() => ({ error: 'Access denied' }));
          onError(errorData.error || 'You don\'t have permission to access this form.');
        }
      } catch (error) {
        console.error('Error verifying user access:', error);
        onError('Unable to verify access. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    verifyUserAccess();
  }, [userId, onVerified, onError]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return null;
};

export const AccessDeniedCard: React.FC<{ userId: string | null; accessError: string | null }> = ({
  userId,
  accessError
}) => (
  <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-4">
    <Card className="max-w-md w-full">
      <CardHeader>
        <CardTitle className="text-destructive">
          {!userId ? 'Missing User ID' : 'Access Denied'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p>{accessError || 'User ID parameter is required to access this form.'}</p>
      </CardContent>
    </Card>
  </div>
);